import { useState, useMemo, useCallback, useRef, FC } from 'react';
import { StyledTextAreaWrapper } from './style';
import useTexts from './useTexts';

const DEFAULT_CHARACTER_LIMIT = 5000;

interface TextAreaProps {
  value: string;
  setValue: (value: string) => void;
  placeholder?: string;
  testId?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  onClick?: () => void;
  limit?: number;
  maxRows?: number;
  isError?: boolean;
}

const TextArea: FC<TextAreaProps> = ({
  value,
  setValue,
  placeholder,
  testId = 'text-area',
  onFocus,
  disabled,
  onClick,
  onBlur,
  maxRows = 5,
  limit = DEFAULT_CHARACTER_LIMIT,
  isError,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { enterTextPlaceholder } = useTexts();

  const handleMergeReasonChange = useCallback(
    (e) => {
      const nextValue = e.target.value;
      if (nextValue?.length <= limit) {
        setValue?.(e.target.value);
      }
    },
    [limit, setValue],
  );

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleWrapperClick = () => {
    onClick?.();
    textAreaRef.current?.focus();
  };

  const isFocusedStyle = useMemo(() => !!value?.length || isFocused, [value, isFocused]);
  const characterCounterValue = useMemo(() => `${value?.length || 0} / ${limit}`, [value, limit]);

  return (
    <StyledTextAreaWrapper
      isIdle={!isFocusedStyle}
      data-testid={`${testId}-wrapper`}
      data-focused={isFocusedStyle}
      onClick={handleWrapperClick}
      isDisabled={disabled}
      maxRows={maxRows}
      isError={isError}
    >
      <textarea
        ref={textAreaRef}
        value={value}
        placeholder={placeholder ?? enterTextPlaceholder}
        onChange={handleMergeReasonChange}
        data-testId={testId}
        onFocus={handleFocus}
        onBlur={handleBlur}
        readOnly={disabled}
      />
      <span id="character-counter" data-testId={`${testId}-character-counter`}>
        {characterCounterValue}
      </span>
    </StyledTextAreaWrapper>
  );
};

export default TextArea;
